import type {
  ApolloCache,
  ApolloError,
  DefaultContext,
  FetchResult,
  MutationFunctionOptions,
  OperationVariables,
} from '@apollo/client';
import type { BusinessAddressRef } from '@seek/hirer-business-address';
import {
  type HirerPaymentResult,
  OrderError as OrderErrorLegacy,
  PaymentCardUnsupportedError as PaymentCardUnsupportedErrorLegacy,
  PaymentConfirmationError as PaymentConfirmationErrorLegacy,
  PaymentGeneralError as PaymentGeneralErrorLegacy,
  PaymentSavedCreditCardPayError as PaymentSavedCreditCardPayErrorLegacy,
} from '@seek/hirer-ecommerce';
import {
  OrderError,
  PaymentCardUnsupportedError,
  PaymentConfirmationError,
  PaymentGeneralError,
  type PaymentMethodType,
  PaymentSavedCreditCardPayError,
} from '@seek/payments-widget';
import type React from 'react';
import { useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import {
  EVENT_NAME_CONTRACT_PURCHASE_VIEWED,
  OBJECT_INTERACTED,
  PAY_BY_CREDIT_CARD,
  PAY_BY_CREDIT_CARD_LINK,
  PAY_BY_INVOICE,
  PAY_BY_INVOICE_LINK,
} from 'src/app/analytics';
import {
  saveCreditCardClicked,
  saveCreditCardUnchecked,
  savedCreditCardInteracted,
} from 'src/app/analytics/payment';
import {
  CONNECTION_BUSINESS_ADDRESS_ERROR,
  CONNECTION_ERROR,
  ON_CREDIT_HOLD_ERROR,
  CONTINUE_ERROR_SHOWN,
  ERROR_MESSAGE_SHOWN,
  RECURRING_ERROR_SHOWN,
  CONNECTION_SESSION_EXPIRED_ERROR,
} from 'src/app/constants/errorMessages';
import { SECTION_BUSINESS_ADDRESS } from 'src/app/constants/pageSectionName';
import { PaymentOption } from 'src/app/constants/paymentOptions';
import {
  ADOBE_VISITOR_ID,
  SITE_SECTION,
  SUB_SECTION,
} from 'src/app/constants/tealium';
import {
  FEATURE_DATA_ANALYTICS,
  useFeatureToggleData,
} from 'src/app/featureToggle';
import { actionUpdateThinking } from 'src/app/state/modules/app/thinking';
import { actionUpdatePaymentOption } from 'src/app/state/modules/formData/paymentOption';
import { actionUpdateIsJobPosted } from 'src/app/state/modules/session/isJobPosted';
import { actionUpdateIsPaymentInProgress } from 'src/app/state/modules/session/isPaymentInProgress';
import { actionUpdateAdBudgetPurchased } from 'src/app/state/modules/tealium/adBudgetPurchased';
import { actionUpdateAdvertiser } from 'src/app/state/modules/tealium/advertiser';
import { actionUpdateHirerContract } from 'src/app/state/modules/tealium/hirerContract';
import { actionUpdateIsPaidByStoredCreditCard } from 'src/app/state/modules/tealium/isPaidByStoredCreditCard';
import { actionUpdateIsSaveCreditCardChecked } from 'src/app/state/modules/tealium/isSaveCreditCardChecked';
import { actionUpdatePaymentMediumSelected } from 'src/app/state/modules/tealium/paymentMediumSelected';
import { actionUpdatePaymentMethod } from 'src/app/state/modules/tealium/paymentMethod';
import { actionUpdateSeekListingId } from 'src/app/state/modules/tealium/seekListingId';
import { store } from 'src/app/state/store';
import { sendLinkEvent, sendPageError } from 'src/app/utils/analytics';
import { sendUtagLinkEvent } from 'src/app/utils/analytics/utils/sendUtagLinkEvent';
import { getIsBusinessAddressVerified } from 'src/app/utils/getIsBusinessAddressVerified';
import { getSelectedProduct } from 'src/app/utils/getSelectedProduct';
import { log } from 'src/app/utils/logging';
import { ValidationError } from 'src/app/views/components/ValidationError';
import { useScroller } from 'src/app/views/hooks/useScroller';
import { BillingStatus } from 'src/graphql/generated';
import type { Tealium } from 'src/types/appState';
import type AppState from 'src/types/appState';
import type { DispatchDraftResponse } from 'src/types/draft';
import { TealiumContractType, TealiumPricingType } from 'src/types/tealium';
import type { TealiumEventData } from 'src/types/trackingData';

import { useGenerateJobIdempotencyKey } from '../../../hooks/useGenerateJobIdempotencyKey';
import { Payments } from '../components/payments';
interface Props {
  businessAddressRef: React.RefObject<BusinessAddressRef>;
  handleDispatchDraft: (
    isCompleted?: boolean,
    rejectOnError?: boolean,
  ) => DispatchDraftResponse;
  handleValidation: (skipIfNull?: boolean) => string[];
  orderExpired: boolean;
  isPaymentDetailsHidden: boolean;
  setDisplayPageError: React.Dispatch<React.SetStateAction<boolean>>;
  setOrderExpired: React.Dispatch<React.SetStateAction<boolean>>;
  updateBusinessAddress: (
    options?:
      | MutationFunctionOptions<
          any,
          OperationVariables,
          DefaultContext,
          ApolloCache<any>
        >
      | undefined,
  ) => Promise<FetchResult<any, Record<string, any>, Record<string, any>>>;
  updateBusinessAddressError?: ApolloError;
}

export const PaymentsSection = ({
  businessAddressRef,
  handleDispatchDraft,
  orderExpired,
  isPaymentDetailsHidden,
  setDisplayPageError,
  setOrderExpired,
  updateBusinessAddress,
  updateBusinessAddressError,
}: Props): JSX.Element => {
  const {
    addressLine1,
    addressLine2,
    adBudgetId,
    advertiserNation,
    billingStatus,
    isBusinessAddressVerified,
    buyUpSell,
    cart,
    countryCode,
    hidePrice,
    isJobPosted,
    isPaymentInProgress,
    jobCreationInProgressCount,
    jobId,
    jobIdempotencyKey,
    postcode,
    productTotalPrice,
    paymentOption,
    productSet,
    productSetStatus,
    rrFailedToSave,
    seekBillingAccountId,
    seekAdvertiserId,
    seekListingId,
    selectedProductId,
    state: addressState,
    suburb,
    tealium,
  } = useSelector(
    (state: AppState) => ({
      addressLine1: state.app.businessAddress.addressLine1,
      addressLine2: state.app.businessAddress.addressLine2,
      adBudgetId: state.session.adBudget?.id,
      advertiserNation: state.session.hirerAccount.nation,
      billingStatus: state.session.hirerAccount?.billingStatus,
      isBusinessAddressVerified: getIsBusinessAddressVerified(
        state.session.postalAddress,
      ),
      buyUpSell: state.session.buyUpSell,
      cart: state.session.cart,
      countryCode: state.session.hirerAccount.nation,
      hidePrice: state.session.hirerAccount?.hidePrice,
      isJobPosted: state.session.isJobPosted,
      isPaymentInProgress: state.session.isPaymentInProgress,
      jobCreationInProgressCount: state.app.jobCreationInProgressCount,
      jobId: state.session.job?.id,
      jobIdempotencyKey: state.session.job?.idempotencyKey,
      postcode: state.app.businessAddress.postcode,
      productSet: state.session.productSet,
      productSetStatus: state.session.productSetStatus,
      productTotalPrice: state.tealium.productTotalPrice,
      paymentOption: state.formData.paymentOption,
      rrFailedToSave: state.session.rrFailedToSave,
      seekAdvertiserId: state.session.seekAdvertiserId,
      seekBillingAccountId: state.session.billingAccountId,
      seekListingId: state.tealium.seekListingId,
      selectedProductId: state.formData.selectedProductId,
      suburb: state.app.businessAddress.suburb,
      state: state.app.businessAddress.state,
      tealium: state.tealium,
    }),
    shallowEqual,
  );
  const dispatch = useDispatch();

  const scrollToSection = useScroller();

  const generatedIdempotencyKey = useGenerateJobIdempotencyKey();
  const isPageAnalyticsEnabled = useFeatureToggleData(FEATURE_DATA_ANALYTICS);

  const [errorCount, setErrorCount] = useState<number>(0);

  const getAdditionalErrorFields = () => ({
    adBudgetId,
    advertiserNation,
    billingStatus,
    isBusinessAddressVerified,
    buyUpSell,
    cartOrderId: cart.orderId,
    countryCode,
    hidePrice,
    isJobPosted,
    jobCreationInProgressCount,
    jobId,
    paymentOption,
    productTotalPrice,
    rrFailedToSave,
    seekAdvertiserId,
    seekListingId,
    selectedProductId,
    productSetId: productSet?.id,
    productSetStatus,
  });

  /*
   * Handle error gracefully and show error message
   */
  const onPostAdButtonClickFailed = (error: Error, isHandled?: boolean) => {
    dispatch(actionUpdateIsPaymentInProgress(false));
    if (error instanceof ValidationError) {
      scrollToSection(SECTION_BUSINESS_ADDRESS);
      return;
    }

    dispatch(actionUpdateThinking(false));
    log.error(
      `PayAndPost | Error Making Payment | ${
        isHandled ? 'Handled' : 'Unhandled'
      } | ${error?.name ?? 'Unknown'}`,
      {
        error,
        isHandled,
        ...getAdditionalErrorFields(),
      },
    );
    const errorMessage = error && error.message;

    const pageError = getPageErrorMessage();

    if (!isPageAnalyticsEnabled) {
      sendPageError(tealium, pageError || errorMessage);
    }

    if (!isHandled) {
      setDisplayPageError(true);
    }
  };

  /**
   * This function will throw an error if any of the steps failed
   * Error is thrown to stop payment execution in the new payment widget
   */
  const onPostAdButtonClicked = async ({
    paymentMethod,
    paymentMedium,
    isSavedCreditCard,
  }: {
    paymentMethod: PaymentMethodType;
    paymentMedium?: string;
    isSavedCreditCard?: boolean;
  }) => {
    if (isPaymentInProgress) {
      return;
    }

    dispatch(
      actionUpdateIsPaidByStoredCreditCard(
        paymentMethod === 'CreditCard' && Boolean(isSavedCreditCard),
      ),
    );

    dispatch(actionUpdatePaymentMediumSelected(paymentMedium || null));
    dispatch(actionUpdateIsPaymentInProgress(true));

    const validationResults =
      await businessAddressRef.current?.validateAllFields();

    if (!validationResults) {
      await handleDispatchDraft(false, true);
      await updateBusinessAddressIfUnverified();
      await updateAdbudget();
      await sendPostAdEvent();
      await processPrepaymentCheck();
      dispatch(actionUpdateThinking(true));
    } else {
      sendValidationErrorEvent(validationResults);
      throw new ValidationError('Validation failed');
    }
  };

  const handledCancelPayment = () => {
    dispatch(actionUpdateIsPaymentInProgress(false));
  };

  const processPrepaymentCheck = () => {
    if (billingStatus === BillingStatus.ON_CREDIT_HOLD) {
      throw new Error('Hirer on credit hold');
    }

    if (paymentOption === PaymentOption.INVOICE) {
      if (generatedIdempotencyKey !== jobIdempotencyKey) {
        throw new Error('Job idempotency key mismatch');
      }
    }
  };

  const sendPostAdEvent = () => {
    const newTealium = store.getState().tealium as Tealium;

    if (!isPageAnalyticsEnabled) {
      sendUtagLinkEvent('caja_post_ad_pressed', {
        adobeVisitorId: ADOBE_VISITOR_ID,
        draftId: newTealium.draftId,
        linkName: 'caja-post-ad-clicked',
        objectInteraction: 'checkout-continue',
        page: newTealium.page,
        discountCode: cart.promoCode,
        seekAdvertiserId,
        siteSection: SITE_SECTION,
        subSection: SUB_SECTION,
        testingId: newTealium.testingId,
        userId: newTealium.userId,
      });
    }
  };

  const updateAdbudget = () => {
    if (adBudgetId) {
      const { adBudgetValue } = tealium;
      const selectedProduct = getSelectedProduct(selectedProductId, productSet);

      dispatch(
        actionUpdateAdBudgetPurchased(EVENT_NAME_CONTRACT_PURCHASE_VIEWED),
      );
      dispatch(
        actionUpdateAdvertiser({
          type: TealiumContractType.Contract,
          pricingType: TealiumPricingType.Variable,
        }),
      );
      dispatch(
        actionUpdateHirerContract({
          adType: selectedProduct?.name,
          value: adBudgetValue,
          balance: adBudgetValue - Number(productTotalPrice),
          type: 'seek-contract',
        }),
      );
    }
  };

  const updateBusinessAddressIfUnverified = async () => {
    setErrorCount(0);

    // update business address if not already verified
    if (
      businessAddressRef &&
      businessAddressRef.current &&
      !isBusinessAddressVerified
      // business address inputs already validated
      // before calling this function from businessAddressRef.current?.validateAllFields()
    ) {
      await updateBusinessAddress({
        variables: {
          input: {
            useForBillingAddress: true,
            addressLine1,
            addressLine2,
            suburb,
            state: addressState,
            postCode: postcode,
            countryCodeAlpha2: countryCode,
            seekBillingAccountId,
            seekId: seekAdvertiserId,
          },
        },
      });
    }
  };

  const sendValidationErrorEvent = (errorMessages: string[]) => {
    if (!isPageAnalyticsEnabled) {
      const eventData = {
        linkName: ERROR_MESSAGE_SHOWN,
        eventName: [ERROR_MESSAGE_SHOWN, CONTINUE_ERROR_SHOWN],
        errorMessage: errorMessages.join(','),
      };

      if (errorCount >= 1) {
        eventData.eventName.push(RECURRING_ERROR_SHOWN);
      }

      setErrorCount(errorCount + 1);

      sendLinkEvent(tealium, eventData);
    }
  };

  const getPageErrorMessage = () => {
    if (updateBusinessAddressError) {
      return CONNECTION_BUSINESS_ADDRESS_ERROR;
    } else if (orderExpired) {
      return CONNECTION_SESSION_EXPIRED_ERROR;
    } else if (billingStatus === BillingStatus.ON_CREDIT_HOLD) {
      return ON_CREDIT_HOLD_ERROR;
    }

    return CONNECTION_ERROR;
  };

  const handlePaymentOptionChange = (
    _selectedIndex: number,
    selectedItem?: string,
  ) => {
    if (!selectedItem) {
      return;
    }

    dispatch(actionUpdatePaymentOption(selectedItem));
    dispatch(actionUpdatePaymentMethod(selectedItem));

    if (!isPageAnalyticsEnabled) {
      const eventData: TealiumEventData = { linkName: '', eventName: [] };
      if (selectedItem === PaymentOption.CREDIT_CARD) {
        eventData.linkName = PAY_BY_CREDIT_CARD_LINK;
        eventData.eventName = [OBJECT_INTERACTED, PAY_BY_CREDIT_CARD_LINK];
        eventData.objectInteraction = PAY_BY_CREDIT_CARD;
      } else if (selectedItem === PaymentOption.INVOICE) {
        eventData.linkName = PAY_BY_INVOICE_LINK;
        eventData.eventName = [OBJECT_INTERACTED, PAY_BY_INVOICE_LINK];
        eventData.objectInteraction = PAY_BY_INVOICE;
      }
      sendLinkEvent(tealium, eventData);
    }
  };

  const onPaymentSuccess = async (result: HirerPaymentResult) => {
    if (result?.status !== 'succeeded') {
      log.error('PayAndPost | onPaymentError', {
        result,
        ...getAdditionalErrorFields(),
      });
    }

    try {
      await handleDispatchDraft(true);
    } catch (error) {
      log.error(`PayAndPost | on${paymentOption}PaymentSuccess DispatchDraft`, {
        error,
        ...getAdditionalErrorFields(),
      });
    }
    dispatch(actionUpdateSeekListingId(String(jobId)));
    dispatch(actionUpdateIsJobPosted(true));
    dispatch(actionUpdateIsPaymentInProgress(false));
  };

  const onPaymentFailed = (error: Error) => {
    dispatch(actionUpdateThinking(false));
    dispatch(actionUpdateIsPaymentInProgress(false));
    const expectedError = [
      PaymentCardUnsupportedError,
      PaymentConfirmationError,
      PaymentGeneralError,
      PaymentSavedCreditCardPayError,

      // TO-DO: Remove this once payments widget is rolled out to 100%
      PaymentCardUnsupportedErrorLegacy,
      PaymentConfirmationErrorLegacy,
      PaymentGeneralErrorLegacy,
      PaymentSavedCreditCardPayErrorLegacy,
    ].find((err) => error instanceof err);
    const metadata = getAdditionalErrorFields();

    if (!expectedError) {
      const isKnownError =
        error instanceof OrderError || error instanceof OrderErrorLegacy;

      if (isKnownError && error.errorCode === 'order_expired') {
        setOrderExpired(true);
        log.error(
          `PayAndPost | on${paymentOption}PaymentError | Order Expired`,
          {
            error,
            metadata,
          },
          true,
        );
      } else if (isKnownError && error.errorCode === 'promocode_invalid') {
        log.error(
          `PayAndPost | on${paymentOption}PaymentError | Invalid Promo`,
          {
            error: 'Failed promotion',
            metadata,
          },
          true,
        );
      } else {
        log.error('PayAndPost | onPaymentError', {
          error,
          metadata,
        });
      }

      setDisplayPageError(true);
    }

    log.error(`PayAndPost | on${paymentOption}PaymentError`, {
      error,
      metadata,
    });
  };

  const onSaveCardOptionChanged = (shouldSave: boolean) => {
    dispatch(actionUpdateIsSaveCreditCardChecked(Boolean(shouldSave)));
    if (!isPageAnalyticsEnabled) {
      sendLinkEvent(
        tealium,
        shouldSave ? saveCreditCardClicked : saveCreditCardUnchecked,
      );
    }
  };

  const onSavedCardLoaded = () => {
    dispatch(actionUpdatePaymentMethod(PaymentOption.SAVED_CREDIT_CARD));
  };

  const onReplaceSavedCard = () => {
    dispatch(actionUpdatePaymentMethod(PaymentOption.CREDIT_CARD));

    if (!isPageAnalyticsEnabled) {
      sendLinkEvent(tealium, savedCreditCardInteracted);
    }
  };

  return (
    <Payments
      handlePaymentOptionChange={handlePaymentOptionChange}
      handlePayAndPostButtonClick={onPostAdButtonClicked}
      onPostAdButtonClickFailed={onPostAdButtonClickFailed}
      handledCancelPayment={handledCancelPayment}
      isPaymentDetailsHidden={isPaymentDetailsHidden}
      onPaymentError={onPaymentFailed}
      onPaymentSuccess={onPaymentSuccess}
      onReplaceSavedCard={onReplaceSavedCard}
      onSaveCardOptionChanged={onSaveCardOptionChanged}
      onSavedCardLoaded={onSavedCardLoaded}
    />
  );
};
